import type { DaySummary } from '@/shared/api/types';
import { parseIsoDate } from '@/shared/format/dates';
import { formatKcal, isRange } from '@/shared/format/nutrition';

const weekday = new Intl.DateTimeFormat('es-AR', { weekday: 'narrow' });

/**
 * El consumo día por día del período.
 *
 * Las barras de días que contienen estimaciones de foto van rayadas con la misma trama que el
 * tramo incierto de la barra del día: es el mismo hecho —"esto es una estimación"— y merece el
 * mismo signo visual en los dos lugares. Un día sin registros no es un día de cero calorías, así
 * que va vacío y punteado en vez de una barra de altura cero.
 */
export function DailyBars({ days, goalKcal }: { days: DaySummary[]; goalKcal: number | null }) {
  const peak = Math.max(goalKcal ?? 0, ...days.map((day) => day.consumed.kcalHigh), 1);

  return (
    <div className="stack" style={{ gap: 'var(--space-2)' }}>
      <div className="bars">
        {days.map((day) => {
          const empty = day.meals.length === 0;
          const height = empty ? 100 : Math.max(3, (day.consumed.kcal / peak) * 100);

          return (
            <div
              key={day.date}
              className={
                empty ? 'bar bar-empty' : isRange(day.consumed) ? 'bar bar-uncertain' : 'bar'
              }
              style={{ height: `${height}%` }}
              role="img"
              aria-label={
                empty
                  ? `${day.date}: sin registros`
                  : `${day.date}: ${formatKcal(day.consumed.kcal)} kcal`
              }
            />
          );
        })}
      </div>

      <div className="bars" style={{ height: 'auto' }}>
        {days.map((day) => (
          <span key={day.date} className="bar-label">
            {weekday.format(parseIsoDate(day.date))}
          </span>
        ))}
      </div>

      <div className="row" style={{ gap: 'var(--space-4)' }}>
        <span className="row nano muted">
          <span className="bar" style={{ width: 14, height: 8, borderRadius: 3 }} />
          medido
        </span>
        <span className="row nano muted">
          <span className="bar bar-uncertain" style={{ width: 14, height: 8, borderRadius: 3 }} />
          incluye estimaciones de foto
        </span>
      </div>
    </div>
  );
}
